<template>
  <b-row>
    <b-col cols="12">
      <b-card>
        <b-card-text class="ml-3">
          <h1 class="code-line" data-line-start="0" data-line-end="1">
            <a id="Methodology_0"></a>Methodology
          </h1>
          <p class="has-line-data" data-line-start="2" data-line-end="3">
            The data that is available on the Oceanbolt Data Platform is
            generated through a range of different sources and processing
            methodologies. This document explains the basis of how each of the
            data products are generated, and the methodology behind the numbers
            you see in the platform.
          </p>
                  </b-card-text>
      </b-card>
            <b-card>
        <b-card-text class="ml-3">
          <h3 class="code-line" data-line-start="4" data-line-end="5">
            <a id="Input_Data_sources_4"></a>Input Data sources
          </h3>
          <h5 class="code-line" data-line-start="6" data-line-end="7">
            <a id="AIS_Data_6"></a>AIS Data
          </h5>
          <p class="has-line-data" data-line-start="7" data-line-end="8">
            The predominant source for the data in the platform is the AIS data emitted by the AIS transponders on board the vessels. The raw AIS data is collected from third-party data providers and  is processed through a range of geospatial algorithms. Our geospatial algorithms draw conclusions about vessel behavior and cargo status.
            <br>
            A raw AIS data stream contains two types of information: 1) dynamic data about a vessel’s location, speed and direction, and 2) static voyage-related information such as destination, eta, and draught. The dynamic information is automatically emitted by the vessel, while the static information is manually input by the crew onboard the vessel. We have built a proprietary algorithm that cleans and merges these two data streams to minimize noise.
          </p>
          <h5 class="code-line" data-line-start="9" data-line-end="10">
            <a id="Polygons_9"></a>Polygons
          </h5>
          <p class="has-line-data" data-line-start="10" data-line-end="11">
            AIS data itself does not contain information about when a vessel enters a berth or a port. In order to generate this type of information, we rely on our own internal proprietary Oceanbolt Polygon Geospatial Dataset. This dataset contains information on dry bulk ports, anchorages, berths and shipyards on a global level. By matching the AIS data with our polygon dataset, we are able to establish when a vessel enters or exits a polygon (such as a port or a berth). In addition to knowing these entry/exit events, we also capture commodity and company information at the berth level and this information forms the basis for our cargo prediction models.

          </p>
          <p class="has-line-data" data-line-start="12" data-line-end="13">
            Examples of polygons for Port Hedland at different resolutions:
          </p>
          <p class="has-line-data" data-line-start="14" data-line-end="15">
            Anchorage:
            <br><img v-img src="img/hedland_low_polygons.png" alt="Anchorages" width="50%">    
          </p>
          <p class="has-line-data" data-line-start="16" data-line-end="17">
            Port
            <br><img v-img src="img/hedland_polygons.png" alt="Ports" width="50%">

          </p>
          <p class="has-line-data" data-line-start="18" data-line-end="19">
            Berth:
            <br><img v-img src="img/hedland_high_polygons.png" alt="Berths"  width="50%">
          </p>
          <h5 class="code-line" data-line-start="21" data-line-end="22">
            <a id="Vessel_Data_21"></a>Vessel Data
          </h5>
          <p class="has-line-data" data-line-start="22" data-line-end="23">
            Finally, we maintain a database of vessels, which helps us understand
            and categorize the data on a segment/owner level. This database also
            forms the basis of the volume calculations on the vessel level.
          </p>
                            </b-card-text>
      </b-card>
            <b-card>
        <b-card-text class="ml-3">
          <h3 class="code-line" data-line-start="25" data-line-end="26">
            <a id="Data_Types_25"></a>Data Types and Algorithms
          </h3>
          <h5 class="code-line" data-line-start="27" data-line-end="28">
            <a id="Trade_flows_27"></a>Trade flows
          </h5>
          <p class="has-line-data" data-line-start="28" data-line-end="29">
            Our trade flow algorithm is the central algorithm on the platform, and it forms the basis for all trade flow and vessel status data. The trade flow algorithm works by parsing events for vessels such as when a vessel enters/exits a polygon or when it changes its draught or destination. Based on these events, the trade flow algorithm infers current cargo status and historical voyages on a vessel level. These voyages are then aggregated to form segment, port, and country level totals.
          </p>
          <h5 class="code-line" data-line-start="30" data-line-end="31">
            <a id="Congestion_30"></a>Congestion
          </h5>
          <p class="has-line-data" data-line-start="31" data-line-end="32">
            The congestion data is generated by processing events for when vessels enter or exit anchorages. For a vessel to be considered “congested”, the following conditions must hold: 1) The vessels must have been inside an anchorage polygon for &gt;6 hours and have a
            speed of &lt; 2/knots. The data is broken down by segment, port, region and country. In addition to vessel counts, the congestion data also contains metrics on congested DWT, average waiting time and median waiting time.
          </p>
          <h5 class="code-line" data-line-start="33" data-line-end="34">
            <a id="Tonnage_Counts_33"></a>Tonnage Counts
          </h5>
          <p class="has-line-data" data-line-start="34" data-line-end="35">
            The tonnage count data considers all vessels that are currently
            inside the respective zone, regardless of whether the vessel is in
            port or at sea. The data is broken down by segment, zone, direction
            and laden status.
          </p>
          <h3 class="code-line" data-line-start="36" data-line-end="37">
            <a id="Classifications_36"></a>Classifications
          </h3>
          <h5 class="code-line" data-line-start="38" data-line-end="39">
            <a id="Vessel_segment_38"></a>Vessel segment
          </h5>
          <p class="has-line-data" data-line-start="39" data-line-end="40">
            We classify the vessels into 4 segments based on DWT:
          </p>
          <ul>
            <li class="has-line-data" data-line-start="41" data-line-end="42">
              Handysize: 10-40k DWT
            </li>
            <li class="has-line-data" data-line-start="42" data-line-end="43">
              Ultramax: 40-70k DWT
            </li>
            <li class="has-line-data" data-line-start="43" data-line-end="44">
              Panamax: 70-110 DWT
            </li>
            <li class="has-line-data" data-line-start="44" data-line-end="46">
              Capesize: 110-450 DWT
            </li>
          </ul>
          <h5 class="code-line" data-line-start="46" data-line-end="47">
            <a id="Regions_46"></a>Regions
          </h5>
          <p class="has-line-data" data-line-start="47" data-line-end="48">
            A region is a loading/discharging area that includes a range of
            different ports, and can span over multiple countries. Examples
            include ECSA, USG, CONT. All ports belong to a region. Regions cover land areas.
          </p>
          <p class="has-line-data" data-line-start="49" data-line-end="50">
            Region Map:
            <br><img v-img src="img/region_map.png" alt="Region map"  width="50%">
          </p>
          <h5 class="code-line" data-line-start="51" data-line-end="52">
            <a id="Zones_51"></a>Zones
          </h5>
          <p class="has-line-data" data-line-start="52" data-line-end="53">
            A zone is a bounded area that is used for tonnage counts. Examples
            include the South Atlantic Ocean, Baltic Sea and Black Sea. Zones cover water areas.
          </p>
          <p class="has-line-data" data-line-start="54" data-line-end="55">
            Zone map:
            <br><img v-img src="img/zone_map.png" alt="Zone map"  width="50%">
          </p>
        </b-card-text>
      </b-card>
    </b-col>
  </b-row>
</template>

<script>
import { mapActions } from "vuex";

export default {
  name: "home",

  data() {
    return {
      auth: {
        apiToken: null,
        user: {},
        admin: false,
        products: []
      },
      rawScreenshot: null,
      searchClient: null
    };
  },
  async mounted() {
    await this.setSearchClient();
    await this.getUserData();
    window.analytics.page("OpenedHome");
  },
  methods: {
    ...mapActions("api", ["getSearchClient", "getUser", "getToken", "logout"]),
    login() {},
    // Log the user out
    async logoutHandler() {
      await this.logout();
    },
    async getUserData() {
      this.auth.apiToken = await this.getToken();
      this.auth.user = await this.getUser();
    },
    async setSearchClient() {
      this.searchClient = await this.getSearchClient();
    }
  }
};
</script>

<style scoped></style>
